import { useQuery } from "@tanstack/react-query";

import companyService from "@services/company.service";
import { ISuggestionCompany } from "@interfaces/company.interface";
import { getNormalizedHostname, isValidUrl } from "@utils/strings.util";
import { COMPANY_SUGGESTIONS_QUERY_KEY, QUERY_CACHE_DEFAULT_EXPIRATION_MS } from "@config/query";

export function useCompanySuggestions(name: string) {
  // The API strips whitespace and rejects an empty name with a 400, so never send a blank query.
  const trimmedName = name.trim();
  const formattedName = isValidUrl(trimmedName)
    ? trimmedName.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "")
    : trimmedName;
  const isEnabled = formattedName.length > 0;
  const searchedHostname = getNormalizedHostname(formattedName);

  const { data, error, isLoading, fetchStatus, isPlaceholderData, refetch } = useQuery({
    enabled: isEnabled,
    queryKey: [COMPANY_SUGGESTIONS_QUERY_KEY, trimmedName],
    queryFn: async () => {
      return companyService.getCompanySuggestions(formattedName);
    },
    select: (data) => {
      const isSearchedWebsite = (company: ISuggestionCompany) =>
        Boolean(searchedHostname) && getNormalizedHostname(company.website) === searchedHostname;

      return [...data].sort((a, b) => {
        const websiteRank = Number(isSearchedWebsite(b)) - Number(isSearchedWebsite(a));
        if (websiteRank !== 0) return websiteRank;

        return Number(Boolean(b.logo)) - Number(Boolean(a.logo));
      });
    },
    staleTime: QUERY_CACHE_DEFAULT_EXPIRATION_MS,
  });

  return { data, error, isLoading, fetchStatus, isPlaceholderData, refetch };
}
